import {useCallback, useEffect, useRef, useState} from 'react';
import {useAuth} from '../../../helpers/AuthContext';

/**
 * Loads and saves the channel's label printer settings: the template registry, the remembered
 * return address, and the last template (GET and PUT /v1/seller/labels/settings).
 */
export default function useLabelSettings() {
  const {fetch: authFetch, API_URL} = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const mounted = useRef(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await authFetch(`${API_URL}/v1/seller/labels/settings`);
      const body = await response.json();
      if (!response.ok) {
        throw new Error(body?.message || body?.error || 'Failed to load label settings');
      }
      if (!mounted.current) return;
      setSettings(body.data);
    } catch (loadError) {
      if (!mounted.current) return;
      setError(loadError.message || 'Failed to load label settings');
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [API_URL, authFetch]);

  /** @param {{returnAddress?: string|null, lastTemplate?: string|null}} changes */
  const save = useCallback(async changes => {
    setSaving(true);
    setSaveError('');

    try {
      const response = await authFetch(`${API_URL}/v1/seller/labels/settings`, {
        method: 'PUT',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(changes),
      });
      const body = await response.json();
      if (!response.ok) {
        throw new Error(body?.message || body?.error || 'Failed to save label settings');
      }
      if (mounted.current) setSettings(body.data);
      return true;
    } catch (failure) {
      if (mounted.current) setSaveError(failure.message || 'Failed to save label settings');
      return false;
    } finally {
      if (mounted.current) setSaving(false);
    }
  }, [API_URL, authFetch]);

  useEffect(() => {
    mounted.current = true;
    load();
    return () => {
      mounted.current = false;
    };
  }, [load]);

  return {settings, loading, error, saving, saveError, save, reload: load};
}
